import type { LocaleCode } from '@avexa/types'
import { useTranslation } from 'react-i18next'
import { normalizeLanguage } from '../i18n'

/**
 * The locale every query is resolved in.
 *
 * `@avexa/api` takes a `LocaleCode` (`'nb' | 'en'`) as an ARGUMENT — category
 * text lives in `category_translations`, one row per locale — and the same
 * value is a segment of every query key, so switching language in the header
 * re-fetches rather than re-rendering stale Norwegian under an English page.
 */
export const useLocale = (): LocaleCode => {
  const { i18n } = useTranslation()
  return normalizeLanguage(i18n.resolvedLanguage ?? i18n.language)
}

/**
 * The first segment of the SEO directory URL, `/:locale/:city/:category`.
 *
 * It is `no`, not `nb`: `nb` is the ISO code for the written standard and
 * belongs in `hreflang`, while the URL is read by people and `avexa.no/no/oslo/
 * elektriker` is what a Norwegian expects to see. The two are deliberately
 * different values, which is why this is a separate function.
 */
export const localeSegment = (locale: LocaleCode): string => (locale === 'en' ? 'en' : 'no')

/** `no` / `nb` → `nb`, anything else → `en`. The inverse of `localeSegment`. */
export const localeFromSegment = (segment: string | undefined): LocaleCode =>
  normalizeLanguage(segment)

export const useLocaleSegment = (): string => localeSegment(useLocale())
